import { Alert, Box, useTheme } from "@mui/material";
import { Link, useLoaderData, useParams } from "react-router-dom";

import {
  ExpandableCopyText,
  PageBreadcrumbs,
  PageLayout,
} from "@/shared/components";
import { EntityComments } from "@/shared/components/comments";
import { CreateDatasetButton } from "@/shared/components/CreateDatasetButton";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { useDataset } from "@/shared/domain/datasets/hooks";
import { useRegisterViewingContext } from "@/shared/services";

import type { DatasetByNameLoaderData } from "../loaders";

import { DatasetHeader } from "./DatasetHeader";
import { DatasetTabRoutes } from "./DatasetTabRoutes";
import { DatasetTabs } from "./DatasetTabs";

export function Dataset() {
  const loaderData = (useLoaderData() as DatasetByNameLoaderData) ?? {};
  const params = useParams();
  const datasetId = loaderData["datasetId"] || params.datasetPath;
  const theme = useTheme();
  const datasetQuery = useDataset(datasetId);

  useRegisterViewingContext("dataset", {
    datasetId,
    record: datasetQuery.data,
  });

  if (!datasetId) {
    return (
      <PageLayout title={`Roboto - Dataset`}>
        <Alert severity="error">Missing dataset id in path</Alert>
      </PageLayout>
    );
  }

  const breadcrumbs = [
    <Link key="list" to="/datasets">
      Datasets
    </Link>,
    <ExpandableCopyText key="datasetId" text={datasetId} trimLength={15} />,
  ];

  return (
    <PageLayout
      title={`Roboto - Dataset - ${datasetId}`}
      headerContentLeft={<PageBreadcrumbs>{breadcrumbs}</PageBreadcrumbs>}
      headerContentRight={<CreateDatasetButton />}
    >
      <ErrorBoundary
        fallback={(props) => {
          if (props.error instanceof Error) {
            return <Alert severity="error">{props.error.message}</Alert>;
          }

          return (
            <Alert severity="error">
              Something went wrong. Please contact{" "}
              <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
              problem persists.
            </Alert>
          );
        }}
      >
        <section
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(2),
          }}
        >
          <DatasetHeader datasetId={datasetId} showTags />
          <DatasetTabs datasetId={datasetId} />
          <DatasetTabRoutes datasetId={datasetId} />
          <Box sx={{ mt: theme.spacing(2) }}>
            <EntityComments entityType={"dataset"} entityId={datasetId} />
          </Box>
        </section>
      </ErrorBoundary>
    </PageLayout>
  );
}
